<template>
  <div class="telephone-bids-table table-component">
    <artisio-table ref="telephoneBidsTable" :pagination="tableData.pagination" :sort="tableData.ordering"
                   :items="tableData.rows" :busy="tableData.loading" :fields="tableFields" :index="false"
                   :checkboxes="true" :search="tableData.search" :enable-simple-search="false"
                   @paramsChanged="getTableData" filter-toggle-string="telephone-bids-table" :extra-data="extraData">

      <template v-slot:header-buttons="{selectedItems}">
        <b-dropdown :text="$t('Actions')" variant="primary" v-permission="'change_auction'">
          <b-dropdown-item v-permission="'change_auction'" @click="onAddTelBidderClick()">
            {{ $t('Manage Tel. Bidder') }}
          </b-dropdown-item>
          <b-dropdown-item v-permission="'change_auction'" @click="onManageClerksClick()">
            {{ $t('Manage Clerks') }}
          </b-dropdown-item>
          <b-dropdown-item :disabled="!tableData.rows.length > 0" v-permission="'change_auction'"
                           @click="onAssignClerksClick()">
            {{ $t('Assign Clerks') }}
          </b-dropdown-item>
        </b-dropdown>

        <b-dropdown :text="$t('Generate PDF')" class="ml-2" variant="primary" :disabled="!tableData.rows.length > 0">
          <b-dropdown-item @click="onGeneratePdfClick()">
            {{ $t('All') }}
          </b-dropdown-item>
          <b-dropdown-item v-for="clerk in sale.clerks" :key="`${clerk.uuid}_pdf`" @click="onGeneratePdfClick(clerk)">
            {{ clerk.name }}
          </b-dropdown-item>
        </b-dropdown>
        <div class="d-inline-block ml-2">
          <h5 class="mb-0 d-inline-block mr-2">{{ $t('View By') }}</h5>
          <b-form-radio-group @change="onViewByChange($event)" v-model="currentViewBy" :options="options" buttons
                              button-variant="outline-primary"/>
        </div>
      </template>
      <template v-slot:search-form-placeholder>
        <search-collapse table-id="telephone-bids-table">
          <telephone-bid-search :update-search-model="updateSearchModel"
                                :get-table-data="getTableData"
                                :vuex-search-model="tableData.searchModel"/>
        </search-collapse>
      </template>
      <template slot="cell(bidder)" slot-scope="{item: item}">
        <template v-if="item">
          <router-link v-if="$hasPermission('view_customer')"
                       :to="{ name: 'customers.dashboard', params: { id: item.customer ? item.customer.uuid : ''}}">
            {{ item.customer ? item.customer.display_name : '' }}
          </router-link>
          <p v-else> {{ item.customer ? item.customer.display_name : '' }} </p>
        </template>
      </template>
      <template slot="cell(paddle_number)" slot-scope="{item: item}">
        {{ item.paddle_number }}
      </template>
      <template slot="cell(bidder_lang)" slot-scope="{item: item}">
        {{ item.language ? item.language.name : '' }}
      </template>
      <template slot="cell(preferred_clerk)" slot-scope="{item: item}">
        {{ item.preferred_clerk ? item.preferred_clerk.name : '' }}
      </template>
      <template slot="cell(contact_number)" slot-scope="{item: item}">
        {{ item.contact_number }}
      </template>
      <template slot="cell(lot)" slot-scope="{item: item}">
        <template v-if="item">
          <router-link v-if="$hasPermission('view_inventoryitem')"
                       :to="{ name: 'inventory.view', params: { id: item.inventory_uuid }}">
            {{ item.lot_no }}
          </router-link>
          <p v-else> {{ item.lot_no }} </p>
        </template>
      </template>
      <template slot="cell(title)" slot-scope="{item: item}">
        <span v-if="item.inventory">{{
            item.inventory.dynamic_fields | attributeByMasterLanguage('title') | striptags
          }}</span>
      </template>
      <template slot="cell(actions)" slot-scope="{item, toggleDetails}">
        <div style="min-height: 2em">
          <b-tooltip :target="`bidder-lots-view-btn-${item.uuid}`" :title="buttonTitle"/>
          <b-button :id="`bidder-lots-view-btn-${item.uuid}`"
                    variant="primary" size="sm" @click="toggleDetails">
            <font-awesome-icon icon="eye"></font-awesome-icon>
          </b-button>
        </div>
      </template>
      <template v-slot:row-details="{item}">
        <div class="mr-1 ml-1">
          <div class="row">
            <div class="artisio-table col-md-12">
              <table class=" table table-lg" v-if="currentViewBy==='lot'">
                <thead>
                <tr>
                  <th class="first-col">{{ $t('Bidder') }}</th>
                  <th>{{ $t('Paddle') }}</th>
                  <th>{{ $t('Bidder Language') }}</th>
                  <th>{{ $t('Preferred Clerk') }}</th>
                  <th>{{ $t('Assigned Clerk') }}</th>
                  <th>{{ $t('Cover Amount') }}</th>
                  <th>{{ $t('Contact Number') }}</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(bid,index) in item.telephone_bids" :key="`lot_${index}`">
                  <td class="first-col">
                    <router-link v-if="$hasPermission('view_customer')"
                                 :to="{ name: 'customers.dashboard', params: { id: bid.bidder ? bid.bidder.customer.uuid : ''}}">
                      {{ bid.bidder ? bid.bidder.customer.display_name : '' }}
                    </router-link>
                    <p v-else> {{ bid.bidder ? bid.bidder.customer.display_name : '' }} </p>
                  </td>
                  <td>{{ bid.bidder ? bid.bidder.paddle_number : '' }}</td>
                  <td>{{ bid.bidder && bid.bidder.language ? bid.bidder.language.name : '' }}</td>
                  <td>{{ bid.bidder && bid.bidder.preferred_clerk ? bid.bidder.preferred_clerk.name : '' }}</td>
                  <td>{{ bid.clerk ? bid.clerk.name : '' }}</td>
                  <td>{{ bid.cover_amount | asCurrency(sale.currency.symbol) }}</td>
                  <td>{{ bid.bidder ? bid.bidder.contact_number : '' }}</td>
                </tr>
                </tbody>
              </table>
              <table class="table table-lg" v-else>
                <thead>
                <tr>
                  <th class="first-col">{{ $t('Lot') }}</th>
                  <th>{{ $t('Title') }}</th>
                  <th>{{ $t('Cover Amount') }}</th>
                  <th>{{ $t('Assigned Clerk') }}</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(bid,index) in item.telephone_bids" :key="`lot_${index}`">
                  <td class="first-col">{{ bid.lot ? bid.lot.lot_no : '' }}</td>
                  <td>
                    <span v-if="bid.lot && bid.lot.inventory">
                      {{ bid.lot.inventory.dynamic_fields | attributeByMasterLanguage('title') | striptags }}
                    </span>
                  </td>
                  <td>{{ bid.cover_amount | asCurrency(sale.currency.symbol) }}</td>
                  <td>{{ bid.clerk ? bid.clerk.name : '' }}</td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </template>
    </artisio-table>
    <telephone-bid-modal/>
    <manage-clerks-modal/>
    <assign-clerks-modal/>
  </div>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import ArtisioTable from "../../core/components/ams-table/ArtisioTable";
  import SearchCollapse from "../../core/components/search-collapse/SearchCollapse";
  import TelephoneBidModal from "./modals/TelephoneBidModal.vue";
  import TelephoneBidSearch from "./TelephoneBidSearch";
  import ManageClerksModal from "@/modules/telephone-bids/modals/ManageClerksModal.vue";
  import AssignClerksModal from "@/modules/telephone-bids/modals/AssignClerksModal.vue";
  import {SOURCE_TELEPHONE_BIDS_TABLE, TELEPHONE_BIDS_TABLE_NAME} from "../constants";
  import i18n from "../../shared/i18n";
  import _ from 'lodash';
  import {ON_PDF_MODAL_DATA_RETRIEVE, ON_PDF_MODAL_OPEN} from "@/plugins/event-bus/event-bus-names";
  import TelephoneBidsService from "@/modules/telephone-bids/TelephoneBidsService";

  const {mapActions, mapState} = createNamespacedHelpers('telephoneBids');

  export default {
    name: "TelephoneBidsTable",
    components: {
      ManageClerksModal,
      ArtisioTable,
      TelephoneBidModal,
      AssignClerksModal,
      SearchCollapse,
      TelephoneBidSearch,
    },
    data() {
      return {
        currentViewBy: 'bidder',
        options: [
          {text: this.$i18n.t('Bidder'), value: 'bidder'},
          {text: this.$i18n.t('Lot'), value: 'lot'},
        ]
      }
    },
    computed: {
      ...mapState({
        tableData: state => state.tableData[state.tableSource],
        sale: state => state.tableData[state.tableSource].sale,
        source: state => state.tableSource,
        viewBy: state => state.viewBy,
      }),
      buttonTitle() {
        if (this.currentViewBy === 'lot')
          return this.$i18n.t('Bidders');
        return this.$i18n.t('Lots');
      },
      tableFields() {
        if (this.currentViewBy === 'lot') {
          return [
            {key: "actions", label: i18n.t(''), sortable: false, tdClass: 'actions-col'},
            {key: "lot", label: i18n.t('Lot'), sortable: true,},
            {key: "title", label: i18n.t('Title'), sortable: true}
          ]
        } else {
          return [
            {key: "actions", label: i18n.t(''), sortable: false, tdClass: 'actions-col'},
            {key: "bidder", label: i18n.t('Bidder'), sortable: true,},
            {key: "paddle_number", label: i18n.t('Paddle'), sortable: true,},
            {key: "bidder_lang", label: i18n.t('Bidder Language'), sortable: true,},
            {key: "preferred_clerk", label: i18n.t('Preferred Clerk'), sortable: true,},
            {key: "contact_number", label: i18n.t('Contact Number'), sortable: true,}
          ]
        }
      },
      extraData() {
        return {
          tableName: TELEPHONE_BIDS_TABLE_NAME
        }
      }
    },
    methods: {
      ...mapActions([
        'getTableData',
        'showModal',
        'removeTelephoneBids',
        'updateSearchModel',
        'showManageClerksModal',
        'updateTableViewBy',
        'updateTableSource',
        'showAssignClerksModal',
        'getTelephoneBidsData']),
      onAddTelBidderClick() {
        this.showModal({
          source: SOURCE_TELEPHONE_BIDS_TABLE,
          object: null,
          sale: _.cloneDeep(this.sale)
        });
      },
      onBatchRemoveClick(items) {
        let uuids = [];
        items.forEach(item => uuids.push(item.uuid));
        this.$confirm(
          this.$i18n.t("Are you sure you want to delete bid(s)?"),
          this.$i18n.t('Bid(s) was successfully deleted'),
          this.removeTelephoneBids,
          uuids
        );
      },
      onManageClerksClick() {
        this.showManageClerksModal({
          source: SOURCE_TELEPHONE_BIDS_TABLE,
          object: _.cloneDeep(this.sale)
        });
      },
      onAssignClerksClick() {

        this.getTelephoneBidsData({auction_uuid: this.sale.uuid})

        this.showAssignClerksModal({
          source: SOURCE_TELEPHONE_BIDS_TABLE,
          object: _.cloneDeep(this.sale)
        });
      },
      async onViewByChange(val) {
        this.currentViewBy = val;
        await this.updateTableViewBy(this.currentViewBy);
        await this.getTableData();
      },
      async onGeneratePdfClick(clerk = '') {
        let params = {}

        if (clerk) {
          params = {
            auction_uuid: this.sale.uuid,
            clerk_uuid: clerk.uuid
          }
        } else {
          params = {
            auction_uuid: this.sale.uuid
          }
        }
        this.$evBus.$emit(ON_PDF_MODAL_OPEN, {
          title: clerk ? this.$i18n.t(`Telephone Bid Listing For {clerk}`, {clerk: clerk.name}) : this.$i18n.t('Telephone Bid Listing For All Clerks')
        });
        let response = await TelephoneBidsService.getTelephoneBidsPdfData(params);
        if (response.success) {
          this.$evBus.$emit(ON_PDF_MODAL_DATA_RETRIEVE, {
            ...response.body,
            filename: response.body.file_name
          })
        }
      },
    },
    mounted() {
      this.updateTableViewBy(this.currentViewBy)
      this.getTableData();
    }
  }
</script>

<style lang="scss" scoped>
  @import "./scss/variables";

  .artisio-table {
    /deep/
    .actions-col {
      width: 35px;
    }

    .first-col {
      padding-left: 35px;
    }

    table tbody tr:last-child td {
      border-bottom: none;
    }
  }
</style>

